import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, hint, ...props }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {hint && <span className="block text-xs text-slate-400 mt-0.5">{hint}</span>}
    <input {...props} className="mt-1.5 w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand-blue" />
  </label>
);

const SOCIALS = ["instagram", "linkedin", "twitter", "facebook", "youtube", "tiktok"];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-slate-400">Loading…</p>;

  const set = (k) => (e) => setS((v) => ({ ...v, [k]: e.target.value }));
  const setSocial = (k) => (e) => setS((v) => ({ ...v, socials: { ...v.socials, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { data: s });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings-view" className="max-w-3xl">
      <h2 className="font-display text-2xl font-bold text-navy">Site Settings</h2>
      <p className="text-slate-500 text-sm mt-1">Edit contact details and links shown across the website. Placeholder values are used until you enter real information.</p>

      <div className="mt-8 space-y-8">
        <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="font-semibold text-navy">Contact details</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Public business email" data-testid="settings-business-email" value={s.business_email || ""} onChange={set("business_email")} placeholder="info@neecdigital.com" />
            <Field label="Phone number" hint="Leave blank to hide from the site" data-testid="settings-phone" value={s.phone || ""} onChange={set("phone")} placeholder="+1 …" />
            <Field label="Website / domain display" data-testid="settings-website" value={s.website_display || ""} onChange={set("website_display")} placeholder="neecdigital.com" />
            <Field label="Form recipient email" hint="Where quote requests are emailed (private)" data-testid="settings-recipient" value={s.form_recipient_email || ""} onChange={set("form_recipient_email")} placeholder="owner@neecdigital.com" />
          </div>
          <Field label="Footer contact text" data-testid="settings-footer-contact" value={s.footer_contact || ""} onChange={set("footer_contact")} />
          <Field label="Main CTA text" data-testid="settings-cta-text" value={s.main_cta_text || ""} onChange={set("main_cta_text")} />
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="font-semibold text-navy">Social links</h3>
          <p className="text-xs text-slate-400 -mt-2">Only links you fill in will appear in the footer.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {SOCIALS.map((k) => (
              <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} data-testid={`settings-social-${k}`}
                value={s.socials?.[k] || ""} onChange={setSocial(k)} placeholder={`https://…`} />
            ))}
          </div>
        </section>
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save-button"
        className="mt-8 inline-flex items-center gap-2 rounded-lg bg-brand-blue px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save settings
      </button>
    </div>
  );
}
